"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  ShopBreadcrumbsProvider,
  useShopBreadcrumbs,
} from "@/integrations/wordpress/WordPressEcommerceShopBreadcrumbs";
import {
  EcommerceTermsProvider,
  useEcommerceTerms,
} from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";

function ArchiveBreadcrumbs() {
  const { links, loading, error } = useShopBreadcrumbs();

  if (loading || error || links.length === 0) {
    return (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/">Home</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Products</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    );
  }

  const lastIndex = links.length - 1;

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {links.map((link, index) => [
          <BreadcrumbItem key={`item-${link.href}`} data-index={index}>
            {index === lastIndex ? (
              <BreadcrumbPage>{link.title}</BreadcrumbPage>
            ) : (
              <BreadcrumbLink asChild>
                <Link to={link.href}>{link.title}</Link>
              </BreadcrumbLink>
            )}
          </BreadcrumbItem>,
          index < lastIndex && (
            <BreadcrumbSeparator key={`sep-${link.href}`} />
          ),
        ])}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function ArchiveTitle() {
  const { links, loading } = useShopBreadcrumbs();
  let title = "Products";

  if (!loading && links && links.length > 0) {
    title = links[links.length - 1].title;
  }

  return (
    <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl font-default text-foreground">
      {title}
    </h1>
  );
}

function ArchiveCategories() {
  const { terms, loading, error } = useEcommerceTerms();

  if (loading || error || terms.length === 0) return null;

  return (
    <div className="mt-8 flex flex-wrap gap-2">
      {terms.map((term, i) => (
        <div key={term.id} data-index={i}>
          <Link
            to={term.link || "#"}
            className="inline-flex items-center justify-center rounded-full border border-border bg-card/50 px-4 py-1.5 text-sm font-medium text-card-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            {term.name}
          </Link>
        </div>
      ))}
    </div>
  );
}

export default function ArchiveTitleSection() {
  const current_wp_term_query_params =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPTermQueryParams()
      : { taxonomy: "product_cat" };
  const wp_term_query = new WP_Term_Query(current_wp_term_query_params);

  return (
    <section
      id="archive-title-section"
      className="bg-background text-foreground py-12 md:py-16 lg:py-20 border-b border-border"
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-4xl">
          <ShopBreadcrumbsProvider>
            <ArchiveBreadcrumbs />
            <ArchiveTitle />
          </ShopBreadcrumbsProvider>

          <EcommerceTermsProvider wp_term_query={wp_term_query}>
            <ArchiveCategories />
          </EcommerceTermsProvider>
        </div>
      </div>
    </section>
  );
}